import { useParams, useNavigate } from 'react-router-dom';
import { usePatientMedicalHistory } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';

export function MedicalTimelinePage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: records, isLoading, isError } = usePatientMedicalHistory(patientId || user?.id || '');

  const isDoctor = user?.role === 'DOCTOR';

  if (isLoading) {
    return <LoadingOverlay containerHeight="min-h-[400px]" message="Carregando histórico médico..." />;
  }

  if (isError) {
    return <ErrorRetry message="Erro ao carregar histórico médico" onRetry={() => navigate(-1)} />;
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    SIGNED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };

  const statusLabels: Record<string, string> = {
    DRAFT: 'Rascunho',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Completo',
    SIGNED: 'Assinado',
    CANCELLED: 'Cancelado',
  };

  return (
    <div className="container max-w-4xl py-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Histórico Médico</h1>
        <p className="text-gray-500 mt-1">
          Todos os prontuários do paciente
        </p>
      </div>

      {!records || records.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Nenhum prontuário encontrado"
          description="Este paciente ainda não possui prontuários médicos registrados"
          action={{ label: 'Voltar', onClick: () => navigate(-1) }}
        />
      ) : (
        <div className="space-y-4">
          {records.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/medical-records/${record.id}`)}
            >
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      {new Date(record.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                    <p className="text-lg font-medium text-gray-900 mt-1">
                      Prontuário #{record.id.slice(0, 8)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[record.status] || 'bg-gray-100 text-gray-800'}`}>
                    {statusLabels[record.status] || record.status}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {record.diagnosisIds && record.diagnosisIds.length > 0 && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                      {record.diagnosisIds.length} diagnóstico(s)
                    </span>
                  )}
                  {record.prescriptionIds && record.prescriptionIds.length > 0 && (
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">
                      {record.prescriptionIds.length} prescrição(ões)
                    </span>
                  )}
                  {record.examRequestIds && record.examRequestIds.length > 0 && (
                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                      {record.examRequestIds.length} exame(s)
                    </span>
                  )}
                  {record.certificateIds && record.certificateIds.length > 0 && (
                    <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs">
                      {record.certificateIds.length} atestado(s)
                    </span>
                  )}
                </div>

                {isDoctor && (
                  <div className="mt-4 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/medical-records/${record.id}`);
                      }}
                    >
                      Ver Prontuário
                    </Button>
                    {record.status !== 'SIGNED' && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/medical-records/${record.id}/edit`);
                        }}
                      >
                        Editar
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
